import React, { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { cartState } from '../atoms/cartAtom';
import { CartService } from '../services/cartService';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineTrash, HiOutlineX, HiOutlineShoppingBag, HiChevronRight, HiPlus, HiMinus } from 'react-icons/hi';
import { useBranding } from '../hooks/useBranding';
import { COLORS } from '../constants';

const CartDrawer = () => {
  const [cart, setCart] = useRecoilState(cartState);
  const { primaryColor, secondaryColor } = useBranding();

  // Create colors object with fallbacks using centralized COLORS
  const colors = {
    primary: primaryColor || '#ff4d00',
    secondary: secondaryColor || '#fc7303'
  };
  
  // Effect to recalculate cart totals when the drawer opens
  useEffect(() => {
    if (cart.isOpen && cart.items?.length > 0) {
      // Recalculate totals on open to ensure consistency
      const count = cart.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const total = cart.items.reduce((sum, item) => {
        const itemPrice = Number(item.price || 0);
        const itemQuantity = Number(item.quantity || 0);
        return sum + (itemPrice * itemQuantity);
      }, 0);
      
      // Only update if values are different to avoid infinite loop
      if (cart.count !== count || cart.total !== total) {
        setCart(prev => ({
          ...prev,
          count,
          total
        }));
      }
    }
  }, [cart.isOpen, cart.items, setCart]);
  
  const handleClose = () => {
    setCart(prev => ({ ...prev, isOpen: false }));
  };
  
  const handleRemoveItem = (cartItemId) => {
    CartService.removeFromCart(cartItemId);
  };
  
  const handleQuantityChange = (cartItemId, newQuantity) => {
    CartService.updateQuantity(cartItemId, newQuantity);
  };
  
  const handleCreateOrder = () => {
    handleClose();
  };
  
  // Helper function to safely format numbers
  const formatPrice = (price) => {
    // Make sure price is a number before using toFixed
    const numericPrice = Number(price);
    return !isNaN(numericPrice) ? numericPrice.toFixed(2) : '0.00';
  };
  
  // Calculate cart total for display (as a backup for cart.total)
  const calculateDisplayTotal = () => {
    if (!cart.items || cart.items.length === 0) return 0;
    
    return cart.items.reduce((sum, item) => {
      const itemPrice = Number(item.price || 0);
      const itemQuantity = Number(item.quantity || 0);
      return sum + (itemPrice * itemQuantity);
    }, 0);
  };
  
  // Use cart.total if valid, otherwise calculate it
  const displayTotal = !isNaN(Number(cart.total)) ? cart.total : calculateDisplayTotal();
  
  return (
    <AnimatePresence>
      {cart.isOpen && (
        <>
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[1000]"
            onClick={handleClose}
          ></motion.div>
          
          {/* Cart drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
            className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-xl z-[1001] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div 
              className="px-6 py-4 text-white flex items-center justify-between sticky top-0"
              style={{
                backgroundColor: colors.primary
              }}
            >
              <h3 className="text-lg font-medium flex items-center">
                <HiOutlineShoppingBag className="mr-2 h-5 w-5" />
                Mon Panier ({cart.count || 0})
              </h3>
              <button
                onClick={handleClose}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Fermer le panier"
              >
                <HiOutlineX className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-grow overflow-y-auto">
              {!cart.items || cart.items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                  <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <HiOutlineShoppingBag className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Votre panier est vide</h3>
                  <p className="text-gray-500 mb-6">Parcourez notre catalogue et ajoutez des produits à votre panier.</p>
                  <Link
                    to="/#products"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none transition-colors"
                    style={{
                      backgroundColor: colors.primary
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.secondary}
                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                    onClick={handleClose}
                  >
                    Explorer les produits
                    <HiChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200 py-2">
                  {cart.items.map((item, index) => {
                    const itemPrice = Number(item.price || 0);
                    const itemQuantity = Number(item.quantity || 1);
                    const itemTotal = itemPrice * itemQuantity;
                    
                    return (
                      <li key={item.cartItemId || `item-${index}`} className="p-4 relative">
                        <div className="flex items-center">
                          {/* Product image */}
                          <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                            <img
                              src={item.image || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E"}
                              alt={item.name || 'Product'}
                              className="h-full w-full object-cover object-center"
                            />
                          </div>
                          
                          {/* Details */}
                          <div className="ml-4 flex-1">
                            <div className="flex justify-between">
                              <h4 className="text-sm font-medium text-gray-900">{item.name || 'Unnamed Product'}</h4>
                              <button
                                onClick={() => handleRemoveItem(item.cartItemId)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                aria-label="Supprimer l'article"
                              >
                                <HiOutlineTrash className="h-4 w-4" />
                              </button>
                            </div>
                            
                            {item.variantName && (
                              <p className="text-xs text-gray-500 mt-0.5">{item.variantName}</p>
                            )}
                            
                            <div className="flex items-center justify-between mt-2">
                              <div>
                                <p 
                                  className="text-sm font-medium"
                                  style={{ color: colors.primary }}
                                >
                                  {formatPrice(itemPrice)} TND
                                </p>
                                <p className="text-xs text-gray-500">
                                  Total: {formatPrice(itemTotal)} TND
                                </p>
                              </div>
                              
                              {/* Quantity controls */}
                              <div className="flex items-center">
                                <button
                                  onClick={() => handleQuantityChange(item.cartItemId, itemQuantity - 1)}
                                  className="p-1 rounded-l-md bg-gray-100 border border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors"
                                  disabled={itemQuantity <= 1}
                                  aria-label="Diminuer la quantité"
                                >
                                  <HiMinus className="h-3 w-3" />
                                </button>
                                <span className="px-3 py-1 border-t border-b border-gray-300 text-sm">
                                  {itemQuantity}
                                </span>
                                <button
                                  onClick={() => handleQuantityChange(item.cartItemId, itemQuantity + 1)}
                                  className="p-1 rounded-r-md bg-gray-100 border border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors"
                                  disabled={itemQuantity >= (item.stock || 100)}
                                  aria-label="Augmenter la quantité"
                                >
                                  <HiPlus className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            
            {/* Footer with summary and checkout button */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 sticky bottom-0">
              {cart.items && cart.items.length > 0 && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-gray-500">Sous-total</span>
                    <span className="text-lg font-medium text-gray-900">{formatPrice(displayTotal)} TND</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    Livraison et taxes calculées à la caisse
                  </p>
                  <Link
                    to="/checkout"
                    className="w-full flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white focus:outline-none transition-colors"
                    style={{
                      backgroundColor: colors.primary
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.secondary}
                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                    onClick={handleCreateOrder}
                  >
                    Passer commande
                    <HiChevronRight className="ml-2 h-5 w-5" />
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;